// src/views/admin/default/components/Announcements.tsx
import { useEffect, useState } from "react";
import { getAnnouncements, deleteAnnouncement } from "services/announcements";
import Card from "components/card";
import Button from "components/button";
import { useNavigate } from "react-router-dom";

interface Announcement {
  id: number;
  title: string;
  content: string;
}

const Announcements = () => {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAnnouncements = async () => {
      try {
        const { data } = await getAnnouncements();
        setAnnouncements(Array.isArray(data?.data) ? data.data : []);
      } catch (error) {
        console.error("Error fetching announcements:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchAnnouncements();
  }, []);

  const navigate = useNavigate();

  const handleDelete = async (id: number) => {
    if (!window.confirm("Are you sure you want to delete this announcement?"))
      return;
    try {
      await deleteAnnouncement(id);
      setAnnouncements((prev) => prev.filter((a) => a.id !== id));
    } catch (error) {
      console.error("Failed to delete announcement:", error);
    }
  };

  return (
    <Card extra="w-full h-full p-4">
      <div className="mb-8 w-full">
        <h4 className="text-xl font-bold text-navy-700 dark:text-white">
          Announcements
        </h4>
      </div>
      {loading ? (
        <p>Loading...</p>
      ) : (
        <div>
          <div className="mb-4 text-right">
            <Button as="link" to="/admin/announcements/create" size="sm">
              Create Announcement
            </Button>
          </div>
          {announcements.length === 0 ? (
            <p className="text-gray-500">No announcements yet.</p>
          ) : (
            announcements.map((announcement) => (
              <div
                key={announcement.id}
                className="mb-4 rounded-md bg-gray-50 p-4 shadow-sm dark:bg-navy-700"
              >
                <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                  <div className="mb-3 md:mb-0">
                    <h5 className="text-lg font-bold text-navy-700 dark:text-white">
                      {announcement.title}
                    </h5>
                    <p className="mt-2 text-base text-gray-600 dark:text-gray-300">
                      {announcement.content}
                    </p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        navigate(`/admin/announcements/${announcement.id}/edit`)
                      }
                    >
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="danger"
                      onClick={() => handleDelete(announcement.id)}
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </Card>
  );
};

export default Announcements;
